import { invoiceSaveErrorKey } from '../utils/invoice-save';
import CommentsPanel from './comments/CommentsPanel';
import useCommentDraftGuard from './comments/useCommentDraftGuard';
import { hasExpenseCommentIdentity, legacyCommentsText } from '../utils/comments-api';
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { observer } from "mobx-react";
import axios from "axios";
import closeIcon2 from '../assets/img/icon-close2.svg';
import { readApiErrorCode } from "../utils/api-error";
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faAngleLeft, faAngleRight, faEllipsisH, faPlusSquare, faSpinner, faSync, faWindowClose, faExclamationTriangle } from '@fortawesome/free-solid-svg-icons';
import Select from 'react-select';

import FormTextAreaAutoSaved from './form-text-area-auto-saved';
import FormTextField from './form-textfield';
import CurrencySelect from './currency-select';
import Modal from './modal';
import Checkbox from "./checkbox";
import Utils from "../utils/utils";

import { fetch } from '../utils/api-wrapper';
import { loadXpensCategories, getXpensCategoryLabel } from '../utils/xpens-categories';
import { resolveDefaultTaxCode, resolveTaxedCode } from '../utils/tax-code-resolvers';
import { getLineItemTableTitle } from '../utils/line-item-table-title';
import Switch from "react-switch";
import SupplierForm from './supplier-form';
import LogHistory from './log-history';
import AsyncSelect from 'react-select/async';
import AutoCompleteField from './field-auto-complete';
import { expandMatterNumber, formatMatterLabelFromData, formatMatterNumber } from '../utils/matter-display';
import AccessControl from "../utils/access_control";
import HoverInfoCard from './hover-card';
import { cachedFetch } from '../utils/cached-api-wrapper';
import { getCachedInvoiceImageUrl } from '../utils/invoice-image-cache';
import { downloadFile } from '../utils/file-manager';
import { buildAttachmentsListUrl, resolveAttachmentsCount } from '../utils/attachments';
import AdditionalFieldsModal from './additional-fields-modal';
import PDFVisualizer from './pdf-visualizer.jsx';
import { withJurisLineItemDescription } from '../utils/juris-line-item-description';
import { resolveSupplierHeaderDefaults, shouldApplySupplierHeaderDefaults } from '../utils/supplier-header-defaults';
import TaxCodeSelector from './tax-code-selector.jsx';
import {
    applyImputationCodingToListItem,
    nonBillableCostTypeOf,
    applyTaxCodeToExpenseJson,
    expenseUsesDisbursementCode,
    imputationDocumentId,
    mongoExtendedJsonId,
    resolveExpenseType,
    resolveImputationCodingFromRows,
    resolveImputationTaxCodeFromRows,
} from '../pages/review-expense/utils/contract-expense-json';
import {
    applyExpenseTypeToExpenseEditorJson,
    applyExpenseTypeToListItem,
    sanitizeNonClientBillableJson,
} from '../pages/review-expense/utils/conditional-fields';
import {
    buildImputationsListUrl,
    deletedImputationOptionLabel,
    formatImputationSelectOptionLabel,
    compareDisplayLabelStrings,
    imputationDisplayLabel,
    resolveStoredImputationMissingFromList,
    sortImputationRowsByDisplayLabel,
} from '../utils/expenses-imputations-config-helpers';
import { resolveCcardReviewPrincipal } from '../utils/c-card-review-principal';
import {
    buildActivityAdditionalFieldsFilter,
    buildBillableAdditionalFieldsFilter,
    buildGlAdditionalFieldsFilter,
    shouldShowActivityInlineTrigger,
} from '../utils/additional-fields-filter-helpers';

const applyImputationCodingToHeader = (
    json,
    { level1Row, leafRow, preserveExistingWhenEmpty = false, nonBillableCostType = '' } = {}
) => {
    const {
        cost_type: costType,
        gl_natural: glNatural,
        gl_office: glOffice,
    } = resolveImputationCodingFromRows({ level1Row, leafRow });
    const nextJson = { ...json };

    if (expenseUsesDisbursementCode(nextJson)) {
        nextJson.CostType = (nonBillableCostType && ![1, true].includes(nextJson.IsBillable))
            ? nonBillableCostType
            : costType || (preserveExistingWhenEmpty ? (json.CostType ?? '') : '');
        nextJson.GLNatural = '';
    } else {
        nextJson.GLNatural = glNatural || (preserveExistingWhenEmpty ? (json.GLNatural ?? '') : '');
        nextJson.CostType = '';
    }

    if (glOffice) {
        nextJson.Office = glOffice;
        nextJson.GLOffice = glOffice;
    } else if (!preserveExistingWhenEmpty) {
        nextJson.Office = '';
        nextJson.GLOffice = '';
    }
    return nextJson;
};

const Transaction = ({ data, statement_id, statementData, selected, close: onClose, account_status, card_type }) => {
    const [statement, setStatement] = useState(statementData);
    const source = axios.CancelToken.source();
    const [pdfBuff, setPdfBuff] = useState(null);
    // PDFVisualizer is always mounted when the viewer is shown (not gated on
    // pdfBuff), so a false start would flash "PDF not available" — start true.
    const [isLoadingPdf, setIsLoadingPdf] = useState(true);
    // Seed for PDFVisualizer's paperclip badge (XPEN-3009). Scoped to the
    // current receipt (statement_id + checksum) — not the whole statement.
    const [attachmentsCount, setAttachmentsCount] = useState(0);
    const attachmentsRequestGenerationRef = useRef(0);
    // PDFVisualizer is a controlled view switcher (document / attachments) —
    // without this, the paperclip calls a no-op default and the panel never opens.
    const [pdfView, setPdfView] = useState("invoice");
    const [transaction, setTransaction] = useState(selected);
    // Keep the latest draft outside React's render cycle so queued saves always
    // use the newest user-entered values, even when an earlier request is pending.
    const transactionRef = useRef(transaction);
    transactionRef.current = transaction;
    const statementIdRef = useRef(statement_id);
    statementIdRef.current = statement_id;
    const saveInFlightRef = useRef(false);
    const savePendingRef = useRef(false);
    const [saveRevision, setSaveRevision] = useState(0);
    const [currentView, setCurrentView] = useState("data");
    const [saving, setSaving] = useState(false);
    const [showDisapproval, setShowDisapproval] = useState(false);
    const [validating, setValidating] = useState(false);
    const [causeDenial, setCauseDenial] = useState(null);
    const [action, setAction] = useState();
    const [savingError, setSavingError] = useState(null);
    const { root: commentRoot, canLeave: canLeaveCommentDraft, onDraftChange } = useCommentDraftGuard(data, setSavingError);
    const close = (...args) => { if (canLeaveCommentDraft()) onClose(...args); };
    const [showConfirmRestartProcess, setShowConfirmRestartProcess] = useState("false");
    const [confirmDeleteViewId, setConfirmDeleteViewId] = useState(null);
    const [billablesSelected, setBillablesSelected] = useState([]);
    const [nonBillablesSelected, setNonBillablesSelected] = useState([]);
    const [confirmDeleteItems, setConfirmDeleteItems] = useState(null);
    const [imputationRoots, setImputationRoots] = useState([]);
    const [imputationChildren, setImputationChildren] = useState([]);
    const [staleImputationRoot, setStaleImputationRoot] = useState(null);
    const [staleImputationLeaf, setStaleImputationLeaf] = useState(null);
    const [categoryEdited, setCategoryEdited] = useState(false);
    const [reviewForUserId, setReviewForUserId] = useState(null);
    const [reviewPrincipalResolved, setReviewPrincipalResolved] = useState(false);
    const [imputationRootsStatus, setImputationRootsStatus] = useState('loading');
    const [imputationRootsErrorKey, setImputationRootsErrorKey] = useState(null);
    const [imputationRootsReloadKey, setImputationRootsReloadKey] = useState(0);
    const imputationRootsFetchGenRef = useRef(0);
    const imputationChildrenFetchGenRef = useRef(0);
    const lastRootResolveKeyRef = useRef(null);
    const lastLeafResolveKeyRef = useRef(null);
    const [rates, setRates] = useState({});
    const [promptNewSupplier, setPromptNewSupplier] = useState(false);
    const [showAdditionalFieldsModal, setShowAdditionalFieldsModal] = useState(false);
    const [workItem, setWorkItem] = useState(null);
    const [additionalFieldsFilter, setAdditionalFieldsFilter] = useState([]);
    const [taxeCodes, setTaxeCodes] = useState([]);
    const [taxesPercentage, setTaxesPercentage] = useState({});
    const [defaultTaxCode, setDefaultTaxCode] = useState(resolveDefaultTaxCode(data.config?.tax_profile));
    const [defaultTaxedCode, setDefaultTaxedCode] = useState(resolveTaxedCode(data.config?.tax_profile));

    const [newSupplier, setNewSupplier] = useState({
        Name: "",
        Number: "",
        Index: "",
        GLNatural: "",
        CostType: "",
        IsBillable: false,
        TaxCode: defaultTaxCode
    });

    useEffect(() => {
        setTaxeCodes(data.config?.tax_profile?.map(t => t.code) ?? []);
        setTaxesPercentage(data.config?.tax_profile?.reduce((acc, t) => {
            acc[t.code] = t.adjusted_rates ?? t.list_rates;
            return acc;
        }, {}));
        setDefaultTaxCode(resolveDefaultTaxCode(data.config?.tax_profile));
        setDefaultTaxedCode(resolveTaxedCode(data.config?.tax_profile));
    }, [data.config]);

    const styleSelect = {
        control: (baseStyles, state) => ({
            ...baseStyles,
            border: "0 !important",
            boxShadow: state.isFocused ? "0px 0px 10px #F39800 " : "0 !important",
            padding: data.isMobile ? "0" : baseStyles.padding,
        })
    };


    const getMatterInfo = (matterId, callback) => {
        if (!matterId) return callback(null);
        cachedFetch(`/matters/search/?text=${matterId}`, "GET", {}, (response) => {
            if (callback && response?.data && response.data.length > 0) {
                return callback(response.data[0]);
            }
            callback(null);
        });
    };

    const updateItemForModal = (val, path1, path2, viewId, fieldName) => {
        const listItem = transaction.json.ListItem ?? [];
        const item = listItem.find(it => it._view_id === viewId);
        if (item) {
            const updatedItem = { ...item, [fieldName]: val };
            updateItem(updatedItem);
            setWorkItem(updatedItem);
        }
    };

    const loadOffices = (search_query = "", callback = () => {
    }) => {
        const val = search_query.trim().replace(/\//g, '');
        const filters = [{ id: "search", value: val }, { id: "id_client", value: data.id_client }];
        const sorting = [{ id: "name", desc: false }];
        const searchData = `?filters=${encodeURIComponent(JSON.stringify(filters ?? []))}&sorting=${encodeURIComponent(JSON.stringify(sorting ?? []))}`;
        fetch(`/offices/${searchData}`, "GET", {}, (response) => {
            callback(response.data.offices.map(o => ({
                label: o.name,
                value: o.name
            })))
        })
    }

    // Aderant /client/options/ loaders. Mirror the invoice.js header pattern
    // (loadAPTypes / loadJurisdictions there) so the same backend contract
    // serves both surfaces. cachedFetch is OK here — the underlying OData
    // tables are synced daily.
    const loadClientOption = (fieldName, search_query = '', callback = () => {}) => {
        const val = (search_query || '').trim().replace(/\//g, '');
        const filters = val.length > 0 ? [{ id: 'Description', value: val }] : [];
        const searchData = `?field_name=${fieldName}&filters=${encodeURIComponent(JSON.stringify(filters))}`;
        cachedFetch(`/client/options/${searchData}`, 'GET', {}, (response) => {
            const rows = (response?.data ?? []).map((r) => ({ value: r.Id, label: r.Description }));
            callback(rows.slice(0, 10));
        });
    };
    const loadAPTypes = (q, cb) => loadClientOption('APType', q, cb);
    const loadJurisdictions = (q, cb) => loadClientOption('Jurisdiction', q, cb);

    // XPEN-2661: resolve a stored Aderant code to the react-select option object
    // {value,label} so the field shows the description; fall back to the code
    // when the supplier value has no matching option.
    const fetchOptionLabel = (fieldName, id) => new Promise((resolve) => {
        cachedFetch(`/client/options/?field_name=${fieldName}&filters=${encodeURIComponent('[]')}`, 'GET', {}, (response) => {
            const row = (response?.data ?? []).find((r) => r.Id === id);
            resolve(row ? { value: row.Id, label: row.Description } : { value: id, label: id });
        });
    });
    const labelizeHeaderIds = (json) => {
        const fields = [['APTypeId', 'APType'], ['JurisdictionId', 'Jurisdiction'], ['PaymentTermId', 'PaymentTerm']];
        return Promise.all(fields.map(([key, field]) => {
            const v = json[key];
            if (v == null || v === '' || typeof v === 'object') return Promise.resolve();
            return fetchOptionLabel(field, v).then((opt) => { json[key] = opt; });
        }));
    };

    const toSelectOption = (stored) => {
        if (stored == null || stored === '') return null;
        // Tolerate a scalar (legacy / extracted) or an object missing its label —
        // fall back to the value so the select never renders blank.
        if (typeof stored === 'object') return { value: stored.value, label: stored.label ?? stored.value };
        return { value: stored, label: stored };
    };

    useEffect(() => () => source.cancel("Transaction unmounted"), []);
    useEffect(() => {
        if (statementData) {
            setStatement(statementData);
            resolveReviewPrincipal(statementData);
        } else {
            fetchStatement();
        }
        fetchRates()
        // The opening statement is a mount-time snapshot supplied with the selected
        // transaction. Later statement refreshes are handled by fetchStatement().
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const fetchStatement = () => {
        const searchData = `?statement_id=${encodeURIComponent(statement_id)}`;

        fetch(`/c_card/statement/${searchData}`, "GET", {},
            (response) => {
                const fetchedStatement = response.data.statement;
                setStatement(fetchedStatement);
                resolveReviewPrincipal(fetchedStatement);
            }
            ,
            (error) => {
                if (error.code === "ERR_NETWORK")
                    fetchStatement();
            }
            , false, source.token);
    }

    const schedulePersist = () => {
        savePendingRef.current = true;
        setSaving(true);
    };

    useEffect(() => {
        if (saving && savePendingRef.current && !saveInFlightRef.current) {
            updateTransaction();
        }
        // Save attempts are intentionally driven by draft and revision changes.
        // Adding updateTransaction would retrigger this effect on every render.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transaction, saving, saveRevision]);

    useEffect(() => {
        if (data.isMobile) {
            // Viewer is not mounted on mobile — clear any leftover buffer/spinner
            // so a mid-session isMobile flip cannot flash a stale receipt.
            setIsLoadingPdf(false);
            setPdfBuff(null);
            return;
        }
        if (transaction.json?.type === 'mileage') {
            // A mileage transaction never shows the PDF viewer, but a pending
            // request from the previous (non-mileage) page could still resolve
            // and get rejected by the staleness guard below without ever
            // clearing isLoadingPdf/pdfBuff.
            setIsLoadingPdf(false);
            setPdfBuff(null);
            return;
        }

        // Pinned so a resolution can tell whether it's still current — two
        // requests can resolve out of order when the user pages between
        // transactions, and a stale one must not overwrite the newer receipt.
        const requestedChecksum = transaction.checksum;
        const requestedStatementId = statement_id;
        setIsLoadingPdf(true);
        // Clear the previous receipt's buffer immediately — otherwise the viewer
        // (and a Download PDF click) can show/save the previous receipt's bytes
        // under the new id while this fetch is still in flight.
        setPdfBuff(null);
        const file_name = transaction.file_name || transaction?.json?.Name || `${statement_id}_${transaction.checksum}.pdf`;
        getCachedInvoiceImageUrl(file_name, source.token)
            .then(url => {
                if (requestedChecksum !== transactionRef.current?.checksum
                    || requestedStatementId !== statementIdRef.current) return;
                if (url) {
                    setPdfBuff(url);
                }
                setIsLoadingPdf(false);
            })
            .catch(() => {
                if (requestedChecksum !== transactionRef.current?.checksum
                    || requestedStatementId !== statementIdRef.current) return;
                setIsLoadingPdf(false);
                setPdfBuff(null);
            });
        // checksum + statement_id identify the document; type/mobile gate the viewer.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [transaction.checksum, statement_id, data.isMobile, transaction.json?.type])

    // XPEN-3009 — seed the paperclip badge for the current credit-card receipt.
    // Mirrors expense.js (XPEN-3007): bump a generation so a stale response from
    // a previous receipt cannot overwrite the current count.
    useEffect(() => {
        attachmentsRequestGenerationRef.current += 1;
        const requestedGeneration = attachmentsRequestGenerationRef.current;

        // Clear immediately so paging between receipts cannot flash the
        // previous document's count under the new ownerSubId.
        setAttachmentsCount(0);

        // PDFVisualizer (and its paperclip) never renders on mobile or for
        // mileage — nothing to seed a badge for.
        if (data.isMobile || transaction.json?.type === 'mileage' || !transaction.checksum || !statement_id) {
            return;
        }

        fetch(
            buildAttachmentsListUrl('cc_receipt', statement_id, transaction.checksum),
            'GET',
            {},
            (response) => {
                if (requestedGeneration !== attachmentsRequestGenerationRef.current) return;
                setAttachmentsCount(resolveAttachmentsCount(response));
            },
            () => {
                if (requestedGeneration !== attachmentsRequestGenerationRef.current) return;
                setAttachmentsCount(0);
            }
        );
    }, [transaction.checksum, statement_id, data.isMobile, transaction.json?.type]);

    // Leave the attachments tab when the user pages to another receipt — the
    // previous document's panel must not stay selected under the new id.
    useEffect(() => {
        setPdfView("invoice");
    }, [transaction.checksum, statement_id]);

    const fetchRates = () => {
        fetch("/expense_rates/", "GET", {}, (response) => {
            const fetchedRates = response.data?.rates;
            setRates(fetchedRates);
        });
    };

    const applyReviewPrincipalResolution = (resolution) => {
        if (resolution.status === 'resolved') {
            const principalChanged = !reviewPrincipalResolved
                || reviewForUserId !== resolution.reviewForUserId;
            setReviewForUserId(resolution.reviewForUserId);
            setReviewPrincipalResolved(true);
            if (principalChanged) {
                setImputationRootsStatus('loading');
                setImputationRootsErrorKey(null);
            }
            return;
        }

        // Do not silently query as the logged-in reviewer when a shared card's
        // coding owner is unknown: review-mode visibility is user-specific.
        imputationRootsFetchGenRef.current += 1;
        setReviewForUserId(null);
        setReviewPrincipalResolved(false);
        setImputationRoots([]);
        setImputationRootsStatus('error');
        setImputationRootsErrorKey('cc_transactions.category_owner_ambiguous');
    };

    const resolveReviewPrincipal = (targetStatement) => {
        const legacyOwnerId = mongoExtendedJsonId(targetStatement?.user?.id_user);
        if (legacyOwnerId) {
            applyReviewPrincipalResolution(resolveCcardReviewPrincipal({
                statementOwnerId: legacyOwnerId,
            }));
            return;
        }

        if (!targetStatement?.card_id) {
            applyReviewPrincipalResolution({ status: 'ambiguous', reviewForUserId: null });
            return;
        }

        fetch(`/c_card/owners/${targetStatement.card_id}`, "GET", {}, (response) => {
            const owners = Array.isArray(response?.data) ? response.data : [];
            const ownerIds = owners.map((owner) => mongoExtendedJsonId(owner?._id)).filter(Boolean);
            const currentUserId = mongoExtendedJsonId(data.id_user);
            applyReviewPrincipalResolution(resolveCcardReviewPrincipal({
                ownerIds,
                currentUserId,
            }));
        }, () => {
            imputationRootsFetchGenRef.current += 1;
            setReviewForUserId(null);
            setReviewPrincipalResolved(false);
            setImputationRoots([]);
            setImputationRootsStatus('error');
            setImputationRootsErrorKey('cc_transactions.category_load_error');
        });
    };

    const retryImputationRoots = () => {
        setImputationRootsStatus('loading');
        setImputationRootsErrorKey(null);
        if (!reviewPrincipalResolved) {
            resolveReviewPrincipal(statement);
            return;
        }
        setImputationRootsReloadKey((value) => value + 1);
    };

    useEffect(() => {
        if (!reviewPrincipalResolved) {
            return;
        }

        const fetchGen = ++imputationRootsFetchGenRef.current;
        setImputationRootsStatus('loading');
        setImputationRootsErrorKey(null);
        const filters = [{ id: 'roots_only', value: true }];
        const expenseType = transaction?.json?.ExpenseType;
        if (expenseType === 'client' || expenseType === 'internal') {
            filters.push({ id: 'client_filter', value: expenseType });
        }

        fetch(buildImputationsListUrl(filters, reviewForUserId, data.id_client), 'GET', {}, (response) => {
            if (fetchGen !== imputationRootsFetchGenRef.current) return;
            const roots = response?.data?.imputations ?? [];
            const hasClientRoots = roots.some((row) => row.origin === 'client');
            const visibleRoots = hasClientRoots ? roots.filter((row) => row.origin === 'client') : roots;
            setImputationRoots(visibleRoots);
            setImputationRootsStatus(visibleRoots.length > 0 ? 'loaded' : 'empty');
            const storedId = transactionRef.current?.json?.ImputationLevel1Id;
            const lookedUpId = storedId != null ? String(storedId) : '';
            const checksumAtResolve = transactionRef.current?.checksum;
            lastRootResolveKeyRef.current = `${checksumAtResolve}:${lookedUpId}`;
            resolveStoredImputationMissingFromList(
                {
                    storedId,
                    liveRows: visibleRoots,
                    reviewForUserId,
                    fetchImpl: fetch,
                },
                (result) => {
                    if (fetchGen !== imputationRootsFetchGenRef.current) return;
                    if (transactionRef.current?.checksum !== checksumAtResolve) return;
                    if (String(transactionRef.current?.json?.ImputationLevel1Id ?? '') !== lookedUpId) return;
                    if (result.action === 'deleted') {
                        setStaleImputationRoot(result.row);
                        setImputationRootsStatus('loaded');
                        return;
                    }
                    // Credit-card review keeps stored ids on action "clear"
                    // (live-but-hidden verify_default). Expense and invoice
                    // review wipe those ids; do not add a clear-write here.
                    if (result.action === 'error') {
                        return;
                    }
                    setStaleImputationRoot(null);
                }
            );
        }, () => {
            if (fetchGen !== imputationRootsFetchGenRef.current) return;
            setImputationRoots([]);
            setImputationRootsStatus('error');
            setImputationRootsErrorKey('cc_transactions.category_load_error');
        });
    }, [reviewPrincipalResolved, reviewForUserId, transaction?.json?.ExpenseType, data.id_client, imputationRootsReloadKey]);

    useEffect(() => {
        if (!reviewPrincipalResolved) {
            return;
        }
        const level1Id = transaction?.json?.ImputationLevel1Id;
        if (!level1Id) {
            imputationChildrenFetchGenRef.current += 1;
            lastLeafResolveKeyRef.current = null;
            setImputationChildren([]);
            setStaleImputationLeaf(null);
            return;
        }

        const fetchParentId = String(level1Id);
        const fetchGen = ++imputationChildrenFetchGenRef.current;
        lastLeafResolveKeyRef.current = null;
        const filters = [{ id: 'parent_id', value: fetchParentId }];
        const expenseType = transaction?.json?.ExpenseType;
        if (expenseType === 'client' || expenseType === 'internal') {
            filters.push({ id: 'client_filter', value: expenseType });
        }

        fetch(buildImputationsListUrl(filters, reviewForUserId, data.id_client), 'GET', {}, (response) => {
            if (fetchGen !== imputationChildrenFetchGenRef.current) return;
            const rows = response?.data?.imputations ?? [];
            const hasClientChildren = rows.some((row) => row.origin === 'client');
            const visibleChildren = hasClientChildren ? rows.filter((row) => row.origin === 'client') : rows;
            setImputationChildren(visibleChildren);
            const leafId = transactionRef.current?.json?.ImputationLeafId;
            const lookedUpId = leafId != null ? String(leafId) : '';
            const checksumAtResolve = transactionRef.current?.checksum;
            lastLeafResolveKeyRef.current = `${checksumAtResolve}:${lookedUpId}`;
            resolveStoredImputationMissingFromList(
                {
                    storedId: leafId,
                    liveRows: visibleChildren,
                    reviewForUserId,
                    fetchImpl: fetch,
                },
                (result) => {
                    if (fetchGen !== imputationChildrenFetchGenRef.current) return;
                    if (transactionRef.current?.checksum !== checksumAtResolve) return;
                    if (String(transactionRef.current?.json?.ImputationLeafId ?? '') !== lookedUpId) return;
                    if (result.action === 'deleted') {
                        setStaleImputationLeaf(result.row);
                        return;
                    }
                    if (result.action === 'error') {
                        return;
                    }
                    setStaleImputationLeaf(null);
                }
            );
        }, () => {
            if (fetchGen === imputationChildrenFetchGenRef.current) setImputationChildren([]);
        });
    }, [reviewPrincipalResolved, reviewForUserId, transaction?.json?.ExpenseType, transaction?.json?.ImputationLevel1Id, data.id_client]);

    useEffect(() => {
        setStaleImputationRoot(null);
        setStaleImputationLeaf(null);
    }, [transaction?.checksum]);

    useEffect(() => {
        if (!reviewPrincipalResolved) return;
        if (lastRootResolveKeyRef.current == null) return;
        const storedId = transaction?.json?.ImputationLevel1Id;
        const lookedUpId = storedId != null ? String(storedId) : '';
        const key = `${transaction?.checksum}:${lookedUpId}`;
        if (lastRootResolveKeyRef.current === key) return;
        lastRootResolveKeyRef.current = key;
        resolveStoredImputationMissingFromList(
            {
                storedId,
                liveRows: imputationRoots,
                reviewForUserId,
                fetchImpl: fetch,
            },
            (result) => {
                if (lastRootResolveKeyRef.current !== key) return;
                if (String(transactionRef.current?.json?.ImputationLevel1Id ?? '') !== lookedUpId) return;
                if (result.action === 'deleted') {
                    setStaleImputationRoot(result.row);
                    setImputationRootsStatus('loaded');
                    return;
                }
                if (result.action === 'error') {
                    return;
                }
                setStaleImputationRoot(null);
            }
        );
    }, [
        reviewPrincipalResolved,
        reviewForUserId,
        imputationRoots,
        transaction?.checksum,
        transaction?.json?.ImputationLevel1Id,
    ]);

    useEffect(() => {
        if (!reviewPrincipalResolved) return;
        if (lastLeafResolveKeyRef.current == null) return;
        const leafId = transaction?.json?.ImputationLeafId;
        const lookedUpId = leafId != null ? String(leafId) : '';
        const key = `${transaction?.checksum}:${lookedUpId}`;
        if (lastLeafResolveKeyRef.current === key) return;
        lastLeafResolveKeyRef.current = key;
        resolveStoredImputationMissingFromList(
            {
                storedId: leafId,
                liveRows: imputationChildren,
                reviewForUserId,
                fetchImpl: fetch,
            },
            (result) => {
                if (lastLeafResolveKeyRef.current !== key) return;
                if (String(transactionRef.current?.json?.ImputationLeafId ?? '') !== lookedUpId) return;
                if (result.action === 'deleted') {
                    setStaleImputationLeaf(result.row);
                    return;
                }
                if (result.action === 'error') {
                    return;
                }
                setStaleImputationLeaf(null);
            }
        );
    }, [
        reviewPrincipalResolved,
        reviewForUserId,
        imputationChildren,
        transaction?.checksum,
        transaction?.json?.ImputationLeafId,
    ]);

    const selectedImputationRoot = useMemo(() => {
        const storedId = transaction?.json?.ImputationLevel1Id;
        if (storedId) {
            const byId = imputationRoots.find((row) => imputationDocumentId(row) === String(storedId));
            if (byId) return byId;
        }
        const legacyCategory = transaction?.json?.Category;
        return imputationRoots.find((row) => row.category === legacyCategory) ?? null;
    }, [imputationRoots, transaction?.json?.ImputationLevel1Id, transaction?.json?.Category]);

    const selectedImputationLeaf = useMemo(() => {
        const storedId = transaction?.json?.ImputationLeafId;
        if (!storedId) return null;
        return imputationChildren.find((row) => imputationDocumentId(row) === String(storedId)) ?? null;
    }, [imputationChildren, transaction?.json?.ImputationLeafId]);

    // XPEN-3187: safe no-op if already cached by App.js's session-level fetch —
    // only matters as a fallback for a direct deep-link before that completes.
    useEffect(() => {
        loadXpensCategories(data);
    }, [data]);

    const categoryLabel = useCallback((category) => getXpensCategoryLabel(data, category), [data]);

    // XPEN-3252: no client category resolved means nothing to show — the raw
    // OCR/AI guess (transaction.json.Category) is not the client's choice and
    // must not be displayed as though it were one. The value stays stored;
    // only the display falls back to null here.
    const categoryValue = useMemo(() => {
        if (selectedImputationRoot) {
            return {
                value: selectedImputationRoot.category,
                label: imputationDisplayLabel(selectedImputationRoot, data),
            };
        }
        const storedId = transaction?.json?.ImputationLevel1Id;
        if (
            staleImputationRoot &&
            storedId &&
            imputationDocumentId(staleImputationRoot) === String(storedId)
        ) {
            return {
                value: staleImputationRoot.category,
                label: deletedImputationOptionLabel(
                    staleImputationRoot,
                    data.t.read('review_expense.imputation_deleted'),
                    data
                ),
                isDeleted: true,
            };
        }
        return null;
    }, [selectedImputationRoot, staleImputationRoot, transaction?.json?.ImputationLevel1Id, data, data.t.lang, data.xpensCategories]);

    const categoryOptions = useMemo(() => {
        const options = [
            { value: data.t.read('n_a'), label: data.t.read('n_a') },
            ...[...imputationRoots]
                .sort((a, b) => compareDisplayLabelStrings(
                    categoryLabel(a.category),
                    categoryLabel(b.category),
                    data.t.lang
                ))
                .map((row) => ({
                    value: row.category,
                    label: categoryLabel(row.category),
                })),
        ];
        const storedId = transaction?.json?.ImputationLevel1Id;
        if (
            staleImputationRoot &&
            storedId &&
            imputationDocumentId(staleImputationRoot) === String(storedId) &&
            !options.some((option) => option.value === staleImputationRoot.category)
        ) {
            options.splice(1, 0, {
                value: staleImputationRoot.category,
                label: deletedImputationOptionLabel(
                    staleImputationRoot,
                    data.t.read('review_expense.imputation_deleted'),
                    data
                ),
                isDeleted: true,
            });
        }
        return options;
    }, [categoryLabel, imputationRoots, data, data.t.lang, data.xpensCategories, staleImputationRoot, transaction?.json?.ImputationLevel1Id]);

    const subcategoryOptions = useMemo(() => {
        const options = sortImputationRowsByDisplayLabel(
            imputationChildren,
            data.t.lang,
            data
        ).map((row) => ({
            value: imputationDocumentId(row),
            label: imputationDisplayLabel(row, data),
            row,
        }));
        const leafId = transaction?.json?.ImputationLeafId;
        if (
            staleImputationLeaf &&
            leafId &&
            imputationDocumentId(staleImputationLeaf) === String(leafId) &&
            !options.some((option) => option.value === String(leafId))
        ) {
            options.unshift({
                value: imputationDocumentId(staleImputationLeaf),
                label: deletedImputationOptionLabel(
                    staleImputationLeaf,
                    data.t.read('review_expense.imputation_deleted'),
                    data
                ),
                isDeleted: true,
                row: staleImputationLeaf,
            });
        }
        return options;
    }, [imputationChildren, data, data.t.lang, data.xpensCategories, staleImputationLeaf, transaction?.json?.ImputationLeafId]);

    const formatDeletedImputationLabel = (option) =>
        formatImputationSelectOptionLabel(
            option,
            data.t.read('review_expense.imputation_deleted')
        );

    const showStatus = (value) => {
        return (value != null && value !== "") || data.config.frontend_invoice == null || !data.config.frontend_invoice.hide_na_fields ? "flex" : "none";
    }

    useEffect(() => {
        setCategoryEdited(false);
    }, [transaction?.checksum]);

    useEffect(() => {
        setTransaction((prev) => {
            const sanitizedJson = sanitizeNonClientBillableJson(prev?.json ?? {});
            if (JSON.stringify(sanitizedJson) === JSON.stringify(prev?.json ?? {})) {
                return prev;
            }
            return { ...prev, json: sanitizedJson };
        });
    }, [transaction?.checksum]);

    const updateTransaction = (action = 'update') => {
        setAction(action);
        const currentTransaction = transactionRef.current;
        const transactionToSave = {
            ...currentTransaction,
            json: sanitizeNonClientBillableJson(currentTransaction?.json ?? {}),
        };
        savePendingRef.current = false;
        saveInFlightRef.current = true;

        fetch("/c_card/transaction/" + statement_id + "/" + (transactionToSave.transaction_id ? transactionToSave.transaction_id : transactionToSave.number), "PUT", transactionToSave,
            response => {
                saveInFlightRef.current = false;
                if (savePendingRef.current) {
                    // Serialize saves so a slower, older request can never overwrite
                    // a newer draft in MongoDB or in the modal state. Trigger the
                    // next effect after React has committed the latest draft.
                    setSaveRevision((revision) => revision + 1);
                    return;
                }
                setSaving(false);
                setSavingError(null);
                setTransaction(response.data);
                fetchStatement();
            },
            error => {
                saveInFlightRef.current = false;
                savePendingRef.current = false;
                setSaving(false);
                //console.error(error);
                setSavingError(data.t.read(invoiceSaveErrorKey(error)))
            })
    }

    const validateTransaction = () => {
        if (!canLeaveCommentDraft()) return;
        setValidating(true);
        const formData = new FormData();
        formData.append("transaction", JSON.stringify({
            ...transaction,
            json: sanitizeNonClientBillableJson(transaction?.json ?? {}),
        }));
        fetch(`/c_card/statement/transaction/validate/${statement_id}/${transaction.checksum}`, "PUT", formData,
            response => {
                setValidating(false);
                setTransaction(response.data);
                getNextTransaction(true, true);
                fetchStatement();
            },
            error => {
                setValidating(false);
                const detail = readApiErrorCode(error);
                if (detail) {
                    setSavingError(`${data.t.read("internal_saving_error")} : ${data.t.read(detail) ?? detail}`);
                } else {
                    setSavingError(data.t.read(invoiceSaveErrorKey(error)));
                }
            })
    }

    const restartProcess = () => {
        fetch(`/c_card/statement/transaction/restart-processing/${statement_id}/${transaction.checksum}`, "GET", {},
            response => {
                close();
            },
            null, false, source.token);
    }

    const isItemBillable = (item) => {
        return item.IsBillable;
    }

    const itemUsesDisbursementCode = (item) =>
        expenseUsesDisbursementCode(transaction.json ?? {}, item);

    const hasDisbursementItems = () =>
        transaction.json?.ListItem?.some(item => itemUsesDisbursementCode(item)) ?? false;

    const hasGlItems = () =>
        transaction.json?.ListItem?.some(item => !itemUsesDisbursementCode(item)) ?? false;

    const resolveLineItemDescriptionSource = (fieldName, fieldValue, callback) => {
        const normalizedFieldValue = fieldValue ?? "";
        if (!Utils.isExportFormat(data, "juris_format") || normalizedFieldValue === "") {
            callback(normalizedFieldValue);
            return;
        }

        const searchUrl = fieldName === "CostType" ? "/cost_type/search/" : "/gl/nat/search/";
        cachedFetch(
            `${searchUrl}?text=${encodeURIComponent(normalizedFieldValue)}`,
            "GET",
            {},
            (response) => {
                const options = Array.isArray(response?.data) ? response.data : [];
                const exactMatch = options.find((option) => option?.name === normalizedFieldValue);
                callback(exactMatch ?? options[0] ?? normalizedFieldValue);
            },
            () => callback(normalizedFieldValue),
            false,
            null,
            { cacheType: "autocomplete" }
        );
    };

    const resolveLineItemDescriptionSourcePromise = (fieldName, fieldValue) => {
        return new Promise((resolve) => {
            resolveLineItemDescriptionSource(fieldName, fieldValue, (descriptionSource) => resolve(descriptionSource));
        });
    };

    const addItem = (billable = false) => {
        if (resolveExpenseType(transaction.json ?? {}) === 'internal' && billable) {
            return;
        }
        const listItem = transaction.json.ListItem ?? [];
        const itemTaxCode = transaction.json.TaxCode ?? defaultTaxedCode;
        const t2 = taxesPercentage[itemTaxCode]?.[1] ?? 0;
        let itemTotal = listItem.length === 0 ? transaction.json.Total : 0;

        if (billable && "BCGSTPSTIN" === transaction.json.TaxCode) {
            itemTotal = itemTotal / (1 + t2);
        }
        const draftIsBillable = billable ? 1 : 0;
        const usesDisbursement = expenseUsesDisbursementCode(
            transaction.json ?? {},
            { IsBillable: draftIsBillable }
        );
        const baseItem = {
            _view_id: listItem.length,
            IsBillable: draftIsBillable,
            TaxCode: itemTaxCode,
            Total: itemTotal,
            ...(usesDisbursement ? { MatterId: transaction.json?.MatterId, WorkDate: transaction.json?.InvoiceDate } : {}),
        };
        const item = applyImputationCodingToListItem(baseItem, transaction.json, {
            level1Row: selectedImputationRoot,
            leafRow: selectedImputationLeaf,
            nonBillableCostType: nonBillableCostTypeOf(data),
        });
        const descriptionFieldName = usesDisbursement ? "CostType" : "GLNatural";
        const descriptionFieldValue = item[descriptionFieldName];
        const supplierName = transaction?.json?.ListSupplier?.[0]?.Name;
        resolveLineItemDescriptionSource(descriptionFieldName, descriptionFieldValue, (descriptionSource) => {
            const itemWithDescription = withJurisLineItemDescription({
                isJurisFormat: Utils.isExportFormat(data, "juris_format"),
                supplierName,
                item,
                fieldName: descriptionFieldName,
                fieldValue: descriptionFieldValue,
                descriptionSource,
            });

            updateItem(itemWithDescription, true);
        });
    }

    const toggleItemSelected = (section, item) => {
        let list = section === "billable" ? billablesSelected : nonBillablesSelected;
        let idx = list.findIndex(it => it._view_id === item._view_id);

        if (idx !== -1) {
            list.splice(idx, 1)
        }
        else {
            list.push(item);
        }

        section === "billable" ? setBillablesSelected([...list]) : setNonBillablesSelected([...list]);

    }

    const deleteItem = (viewId) => {
        const listItem = (transaction.json?.ListItem || []).filter(item => item._view_id !== viewId)
            .map((item, index) => ({
                ...item, // Spread the existing properties
                "_view_id": index,
            }
            ));
        setTransaction(prev => ({ ...prev, json: { ...prev.json, ListItem: [...listItem] } }));
        schedulePersist();
        setConfirmDeleteViewId(null);
        setNonBillablesSelected([])
        setBillablesSelected([])
    }

    const confirmDeleteAction = (section) => {
        let list = section === "billable" ? billablesSelected : nonBillablesSelected;
        setConfirmDeleteItems(list);
        section === "billable" ? setBillablesSelected([]) : setNonBillablesSelected([]);
    }

    const deleteItems = () => {
        const listItem = (transaction.json?.ListItem || []).filter(item => !confirmDeleteItems.includes(item))
            .map((item, index) => ({
                ...item, // Spread the existing properties
                "_view_id": index,
            }
            ));
        setTransaction(prev => ({ ...prev, json: { ...prev.json, ListItem: [...listItem] } }));
        schedulePersist();
        setNonBillablesSelected([])
        setBillablesSelected([])
        setConfirmDeleteItems(null);
    }

    // XPEN-2798: delegate tax computation to xpens-api (like invoice.js / expense.js)
    // so all three surfaces share one rounding + BCGSTPSTIN implementation.
    const computeTaxes = (targetTransaction) => {
        const json = targetTransaction?.json;
        if (!json) return;
        fetch('/utils/compute_taxes', 'POST', json, (response) => {
            setTransaction({ ...targetTransaction, json: response.data.json });
            schedulePersist();
        });
    };

    const updateItem = (item, updateTaxes = false) => {
        let listItem = transaction.json.ListItem ?? [];
        const index = listItem.findIndex(it => it._view_id === item._view_id);
        if (index === -1) {
            listItem.push({ ...item });
        }
        else {
            listItem[index] = { ...item };
        }

        const updatedTransaction = { ...transaction, json: { ...transaction.json, ListItem: [...listItem] } };

        if (updateTaxes) {
            computeTaxes(updatedTransaction);
            return;
        }

        setTransaction(updatedTransaction);
        schedulePersist();
    }

    const toggleSelectAll = (sectionName) => {
        if (sectionName === "non-billable")
            setNonBillablesSelected(transaction.json.ListItem && transaction.json.ListItem.filter(item => !itemUsesDisbursementCode(item)).length !== nonBillablesSelected.length ? [...transaction.json.ListItem.filter(item => !itemUsesDisbursementCode(item))] : [])
        else
            setBillablesSelected(transaction.json.ListItem && transaction.json.ListItem.filter(item => itemUsesDisbursementCode(item)).length !== billablesSelected.length ? [...transaction.json.ListItem.filter(item => itemUsesDisbursementCode(item))] : [])
    }


    /**
     * when getting the next transaction we call ask to remove it from list 
     * @param {boolean} remove 
     * @param {boolean} forward 
     */
    const getNextTransaction = (remove = true, forward = true) => {
        if (!canLeaveCommentDraft()) return;
        let nextIndex;
        setBillablesSelected([])
        setNonBillablesSelected([])
        const indexCurrentInvoice = statement?.transactions.findIndex(
            xpen => xpen.checksum === transaction.checksum
        );

        if (forward === true) {
            if (indexCurrentInvoice + 1 < statement?.transactions.length) {
                nextIndex = indexCurrentInvoice + 1
            }
            else {
                nextIndex = 0
            }
        }

        else if (forward === false) {
            if (indexCurrentInvoice - 1 >= 0)
                nextIndex = indexCurrentInvoice - 1;
            else
                nextIndex = statement?.transactions.length - 1;
        }

        if (remove === true) {
            statement?.transactions.splice(indexCurrentInvoice, 1);
            nextIndex = indexCurrentInvoice;
        }

        if (statement?.transactions.length > nextIndex) {
            setTransaction(statement?.transactions[nextIndex]);
        }
        else
            close();
    }

    const updateTransactionStatus = (choice) => {
        if (!canLeaveCommentDraft()) return;
        if (choice.value === "Validated") {
            validateTransaction();
            return;
        }
        if (choice.value === "Approved") {
            approveTransaction();
            return;
        }
        if (choice.value === "Denied") {
            setShowDisapproval(true);
            return;
        }
        setTransaction(prev => ({ ...prev, status: choice.value }));
        schedulePersist();
    };

    const approveTransaction = () => {
        if (!canLeaveCommentDraft()) return;
        setTransaction(prev => ({ ...prev, status: 'Approved' }));
        setAction('approving');
        schedulePersist();
    }

    const disApproveTransaction = () => {
        if (!canLeaveCommentDraft()) return;
        setAction('disApproving');
        setTransaction(prev => ({
            ...prev,
            status: 'Denied',
            status_details: new Date().toLocaleString() + ' : ' + data.firstName + ' ' + data.lastName + ' : ' + causeDenial + '\n' + transaction?.status_details
        }
        ));
        setShowDisapproval(false);
        schedulePersist();
    }

    const supplierChosen = (sup, typedText) => {

        if (!sup) {
            // XPEN-3267 — the supplier field hides "add it" for tenants that may
            // not create suppliers; this guards any other route to a null pick.
            setPromptNewSupplier(Utils.clientAllowsSupplierCreate(data));
            return;
        }

        const supplierName = sup?.name ?? "";

        setPromptNewSupplier(false);
        const prev = {...transaction}
        const list = prev.json?.ListSupplier || [];

        // XPEN-2661: on a manual supplier pick, fetch the supplier detail in
        // parallel and merge the Aderant header ids into the single write below.
        // Extraction-time population is handled server-side.
        const wantsHeaderDefaults = shouldApplySupplierHeaderDefaults(
            Utils.isExportFormat(data, "aderant"),
            { status: prev.status }
        );
        const detailPromise = wantsHeaderDefaults
            ? new Promise((resolve) =>
                  fetch(`/suppliers/${sup._id}`, 'GET', {}, (r) => resolve(r?.data ?? null), () => resolve(null), false, source.token)
              )
            : Promise.resolve(null);

        const lineItemsPromise = Promise.all((transaction.json.ListItem ?? []).map(async (it) => {
            const nextGLNatural = [undefined, null, ''].includes(sup.gl_code) ? (it.GLNatural ?? "") : sup.gl_code;
            const nextCostType = [undefined, null, ''].includes(sup.cost_type) ? (it.CostType ?? "") : sup.cost_type;
            const nextIsBillable = sup.IsBillable ?? isItemBillable(it);
            const isBillableItem = nextIsBillable === true || nextIsBillable === 1;
            const descriptionFieldName = isBillableItem ? "CostType" : "GLNatural";
            const descriptionFieldValue = isBillableItem ? nextCostType : nextGLNatural;
            const descriptionSource = await resolveLineItemDescriptionSourcePromise(descriptionFieldName, descriptionFieldValue);

            return withJurisLineItemDescription({
                isJurisFormat: Utils.isExportFormat(data, "juris_format"),
                supplierName,
                item: {
                    ...it,
                    GLNatural: nextGLNatural,
                    CostType: nextCostType,
                    IsBillable: nextIsBillable
                },
                fieldName: descriptionFieldName,
                fieldValue: descriptionFieldValue,
                descriptionSource
            });
        }));

        Promise.all([lineItemsPromise, detailPromise]).then(async ([items, supplierDetail]) => {
            let json = {
                ...prev.json,
                GLNatural: [undefined, null, ''].includes(sup.gl_code) ? (prev.json.GLNatural ?? "") : sup.gl_code,
                CostType: [undefined, null, ''].includes(sup.cost_type) ? (prev.json.CostType ?? "") : sup.cost_type,
                IsBillable: sup.IsBillable,
                DueDate: Number.isInteger(sup.payment_terms) && prev.json.InvoiceDate ? new Date(new Date(prev.json.InvoiceDate).setDate(new Date(prev.json.InvoiceDate).getDate() + sup.payment_terms)).toISOString().split('T')[0] : prev.json.DueDate,
                ListItem: items,
                ListSupplier: [
                    { Name: sup.name, Number: sup.number, Index: sup._id, GLNatural: sup.gl_code ?? "", CostType: sup.cost_type ?? "", IsBillable: sup.IsBillable, payment_terms: sup.payment_terms },
                    ...list.slice(1)
                ]
            };

            if (supplierDetail) {
                json = resolveSupplierHeaderDefaults(json, supplierDetail).json;
                await labelizeHeaderIds(json);
            }

            setTransaction({ ...prev, json });
            schedulePersist();
        });
    }

    const updateType = (choice) => {
        setTransaction(prev => ({ ...prev, json: { ...prev.json, type: choice?.value, rate: choice.rate } }));
        schedulePersist();
    }

    const updateCategory = (choice) => {
        const level1Row = imputationRoots.find((row) => row.category === choice?.value) ?? null;
        if (
            !level1Row &&
            staleImputationRoot &&
            choice?.value === staleImputationRoot.category
        ) {
            return;
        }
        setStaleImputationRoot(null);
        setStaleImputationLeaf(null);
        let nextJson = {
            ...transaction.json,
            Category: choice?.value ?? '',
            ImputationLevel1Id: level1Row ? imputationDocumentId(level1Row) : null,
            ImputationLeafId: null,
        };
        nextJson = applyImputationCodingToHeader(nextJson, { level1Row, leafRow: null, nonBillableCostType: nonBillableCostTypeOf(data) });
        nextJson.ListItem = (nextJson.ListItem || []).map((item) => applyImputationCodingToListItem(
            item,
            nextJson,
            { level1Row, leafRow: null, nonBillableCostType: nonBillableCostTypeOf(data) }
        ));
        const taxCode = resolveImputationTaxCodeFromRows({ level1Row, leafRow: null });
        nextJson = applyTaxCodeToExpenseJson(nextJson, taxCode);
        setCategoryEdited(true);
        setTransaction({ ...transaction, json: nextJson });
        schedulePersist();
    };

    const updateSubcategory = (choice) => {
        if (
            staleImputationLeaf &&
            choice?.value &&
            String(choice.value) === imputationDocumentId(staleImputationLeaf)
        ) {
            return;
        }
        setStaleImputationLeaf(null);
        const leafRow = choice
            ? imputationChildren.find((row) => imputationDocumentId(row) === String(choice.value)) ?? null
            : null;
        let nextJson = {
            ...transaction.json,
            ImputationLeafId: leafRow ? imputationDocumentId(leafRow) : null,
        };
        nextJson = applyImputationCodingToHeader(nextJson, {
            level1Row: selectedImputationRoot,
            leafRow,
            nonBillableCostType: nonBillableCostTypeOf(data),
        });
        nextJson.ListItem = (nextJson.ListItem || []).map((item) => applyImputationCodingToListItem(
            item,
            nextJson,
            { level1Row: selectedImputationRoot, leafRow, nonBillableCostType: nonBillableCostTypeOf(data) }
        ));
        const taxCode = resolveImputationTaxCodeFromRows({ level1Row: selectedImputationRoot, leafRow });
        nextJson = applyTaxCodeToExpenseJson(nextJson, taxCode);
        setTransaction({ ...transaction, json: nextJson });
        schedulePersist();
    };

    const updateExpenseType = (choice) => {
        const expenseType = choice?.value ?? '';
        let nextJson = applyExpenseTypeToExpenseEditorJson(transaction.json ?? {}, expenseType);
        nextJson = applyImputationCodingToHeader(nextJson, {
            level1Row: selectedImputationRoot,
            leafRow: selectedImputationLeaf,
            preserveExistingWhenEmpty: true,
            nonBillableCostType: nonBillableCostTypeOf(data),
        });
        nextJson.ListItem = (nextJson.ListItem || []).map((item) => applyImputationCodingToListItem(
            applyExpenseTypeToListItem(item, expenseType),
            nextJson,
            {
                level1Row: selectedImputationRoot,
                leafRow: selectedImputationLeaf,
                preserveExistingWhenEmpty: true,
                nonBillableCostType: nonBillableCostTypeOf(data),
            }
        ));
        setTransaction({ ...transaction, json: nextJson });
        schedulePersist();
    };

    const updateHeaderBillable = (isBillable) => {
        let nextJson = {
            ...transaction.json,
            IsBillable: isBillable,
        };
        nextJson = applyImputationCodingToHeader(nextJson, {
            level1Row: selectedImputationRoot,
            leafRow: selectedImputationLeaf,
            preserveExistingWhenEmpty: true,
            nonBillableCostType: nonBillableCostTypeOf(data),
        });
        nextJson.ListItem = (nextJson.ListItem || []).map((item) => applyImputationCodingToListItem(
            { ...item, IsBillable: isBillable ? 1 : 0 },
            nextJson,
            {
                level1Row: selectedImputationRoot,
                leafRow: selectedImputationLeaf,
                preserveExistingWhenEmpty: true,
                nonBillableCostType: nonBillableCostTypeOf(data),
            }
        ));
        setTransaction({ ...transaction, json: nextJson });
        schedulePersist();
    };

    const recalculateSingleItemReceiptTotal = (targetTransaction, receiptTotal) => {
        if (targetTransaction.json?.ListItem?.length !== 1) return false;
        const numericReceiptTotal = Number(receiptTotal);
        if (!Number.isFinite(numericReceiptTotal) || numericReceiptTotal <= 0) return false;

        const tip = Number(targetTransaction.json.Tip) || 0;
        const taxCode = targetTransaction.json.TaxCode || defaultTaxCode;
        const [rawT1 = 0, rawT2 = 0, rawT3 = 0] = taxesPercentage[taxCode] ?? [];
        const taxPercentage = Number(rawT1 || 0) + Number(rawT2 || 0) + Number(rawT3 || 0);
        const taxDeduction = Number(targetTransaction.json.TaxDeduction) || 0;
        const beforeTip = numericReceiptTotal - tip;
        const beforeTax = taxPercentage !== 0 ? beforeTip / (1 + taxPercentage) : beforeTip;
        const itemTotal = beforeTax + ((beforeTip - beforeTax) * taxDeduction);
        const item = {
            ...targetTransaction.json.ListItem[0],
            Total: itemTotal,
            TaxCode: taxCode,
        };
        computeTaxes({
            ...targetTransaction,
            json: {
                ...targetTransaction.json,
                UnadjustedTotal: numericReceiptTotal,
                BigTotalPayed: numericReceiptTotal,
                Total: itemTotal,
                ListItem: [item],
            },
        });
        return true;
    };

    const updateReceiptTotal = (value) => {
        const numericValue = Number(value) || 0;
        const updatedTransaction = {
            ...transaction,
            json: {
                ...transaction.json,
                UnadjustedTotal: numericValue,
                BigTotalPayed: numericValue,
            },
        };
        if (recalculateSingleItemReceiptTotal(updatedTransaction, numericValue)) return;

        const tip = Number(transaction.json.Tip) || 0;
        setTransaction({
            ...updatedTransaction,
            json: { ...updatedTransaction.json, Total: numericValue - tip },
        });
        schedulePersist();
    };

    const updateTip = (value) => {
        const previousTip = Number(transaction.json?.Tip) || 0;
        const tip = Number(value) || 0;
        const updatedTransaction = {
            ...transaction,
            json: { ...transaction.json, Tip: tip },
        };

        if (previousTip === 0 && updatedTransaction.json.ListItem?.length === 1) {
            const receiptTotal = Number(updatedTransaction.json.BigTotalPayed);
            if (recalculateSingleItemReceiptTotal(updatedTransaction, receiptTotal)) return;
        }

        setTransaction(updatedTransaction);
        schedulePersist();
    };

    const updateLineItemAndDescription = (lineViewId, fieldName, fieldValue, descriptionSource) => {
        const listItems = transaction?.json?.ListItem || [];
        const currentItem = listItems.find((it) => it._view_id === lineViewId);

        if (!currentItem) {
            return;
        }

        const updatedItem = withJurisLineItemDescription({
            isJurisFormat: Utils.isExportFormat(data, "juris_format"),
            supplierName,
            item: currentItem,
            fieldName,
            fieldValue,
            descriptionSource,
        });

        updateItem(updatedItem);
    };
    
    let supplierName = transaction && transaction.json && transaction.json.ListSupplier && transaction.json.ListSupplier[0] && transaction.json.ListSupplier.length > 0 ? transaction.json.ListSupplier[0].Name : '';

    // PDFVisualizer shows the paperclip whenever ownerType+ownerId are set —
    // without a real checksum that would list statement-wide attachments (list
    // omits owner_sub_id). Gate all three owner props together.
    const attachmentsOwnerReady = Boolean(statement_id && transaction?.checksum);

    return transaction == null ?
        <div className="loading-container">
            <FontAwesomeIcon className="infinite-rotate" icon={faSpinner} />
        </div>
        :
        <div ref={commentRoot} className="invoice bg-white">
            {
                // statement_id + checksum identify a credit-card receipt, not an
                // invoice id, so showInvoiceExports is off — the export menu
                // items call an invoice-only backend route that would fail.
                // Always mounted when shown (not gated on pdfBuff) — PDFVisualizer
                // owns loaded/loading/unavailable via pdfBuff+isLoading.
                // XPEN-3009 — attachments are per receipt (this transaction), not
                // grouped across the whole statement. ownerSubId=checksum keeps
                // files tied to this receipt.
                // No confirmDelete — the old hamburger Delete was a dead stub
                // (same omission as the expense migration in XPEN-3006).
                !data.isMobile && transaction.json?.type !== 'mileage' &&
                <PDFVisualizer
                    pdfBuff={pdfBuff}
                    data={data}
                    downloadFile={downloadFile}
                    id={`${statement_id}_${transaction.checksum}`}
                    showInvoiceExports={false}
                    primaryTabLabel={data.t.read("document_tab_label")}
                    filenamePrefix="cc_receipt"
                    isLoading={isLoadingPdf}
                    ownerType={attachmentsOwnerReady ? "cc_receipt" : null}
                    ownerId={attachmentsOwnerReady ? statement_id : null}
                    ownerSubId={attachmentsOwnerReady ? transaction.checksum : null}
                    attachmentsCount={attachmentsCount}
                    pdfView={pdfView}
                    changePdfView={setPdfView}
                />
            }

            <div className={data.isMobile ? "content-section d-flex flex-column mb-5" : "content-section d-flex flex-column"}>
                <div className="d-flex main-tabs">
                    {
                        statement?.transactions && statement?.transactions.length > 1 &&
                        <div className='pointer icon-box'>
                            <FontAwesomeIcon data-testid="transaction-nav-prev" disabled={saving} icon={faAngleLeft} className="fa-2x pointer" color='#F39800' onClick={() => getNextTransaction(false, false)} />
                        </div>
                    }
                    <div className={"flex-grow-1 p-3 tab-title " + (currentView === "data" ? "tab-selected" : "")} onClick={() => setCurrentView("data")}>
                        {data.t.read("data")}
                    </div>
                    <div className={"flex-grow-1 p-3 tab-title " + (currentView === "history" ? "tab-selected" : "")} onClick={() => setCurrentView("history")}>
                        {data.t.read("history")}
                    </div>
                    <div className="p-3 pointer" onClick={() => close()}>
                        <img src={closeIcon2} alt="Close" />
                    </div>
                    {
                        statement?.transactions && statement?.transactions.length > 1 &&
                        <div className='pointer icon-box'>
                            <FontAwesomeIcon data-testid="transaction-nav-next" disabled={saving} icon={faAngleRight} className="fa-2x pointer" color='#F39800' onClick={() => getNextTransaction(false, true)} />
                        </div>
                    }
                </div>
                {
                    currentView === "data" && <div className="p-4 d-flex flex-column flex-grow-1">
                        <div className="row mb-1">
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 align-self-center font-weight-bold"}>
                                {data.t.read("invoice.status_txt")} :
                            </div>
                            <Select
                                name="status"
                                value={transaction.status ? { value: transaction.status, label: data.t.read(`status.${transaction.status.toLowerCase()}`) } : null}
                                options={data.config.invoice_status.map(s => ({ label: data.t.read(`status.${s.toLowerCase()}`), value: s }))}
                                styles={styleSelect}
                                className={data.isMobile ? "col-6 invoice-value pl-1" : "pl-1 col-3 invoice-value"}
                                classNamePrefix="select"
                                isDisabled={true}
                                onChange={updateTransactionStatus}
                            />
                        </div>
                        <div className="row mb-1" hidden={transaction.json?.type !== 'mileage'}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                Type :
                            </div>
                            <Select
                                name="type"
                                value={transaction.json?.type ? { value: transaction.json?.type, label: data.t.read(transaction.json?.type) } : null}
                                options={[{ label: data.t.read('mileage'), value: 'mileage', rate: (rates?.mileage_details?.rate || 0.7) }]}
                                styles={styleSelect}
                                className={data.isMobile ? "col-6 invoice-value pl-1" : " pl-1 col-3 invoice-value"}
                                classNamePrefix="select"
                                onChange={updateType}
                                placeholder={'Type'}
                            />
                        </div>

                        <div className="row" hidden={transaction.json?.type !== 'mileage'}>
                            <div className={data.isMobile ? "col-12 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {data.t.read("address_origin")} :
                            </div>
                            <div className={data.isMobile ? "col-11 invoice-value" : "col-3 invoice-value"}>
                                <AutoCompleteField
                                    data={data}
                                    required
                                    disablePortal
                                    value={transaction.json?.AddressOrigin ?? ""}
                                    onChange={val => setTransaction(prev => ({
                                        ...prev,
                                        json: { ...prev.json, AddressOrigin: val?.name ?? val },
                                    }))}
                                    className="form-control border-0 p-0 mb-2"
                                    searchUrl="/places/search/"
                                    minLength={1}
                                    onParentBlur={() => schedulePersist()}
                                    placeholder={data.t.read("supplier_table.address")}
                                />
                            </div>
                        </div>

                        <div className="row" hidden={transaction.json?.type !== 'mileage'}>
                            <div className={data.isMobile ? "col-12 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {data.t.read("address_destination")} :
                            </div>
                            <div className={data.isMobile ? "col-11 invoice-value" : "col-3 invoice-value"}>
                                <AutoCompleteField
                                    data={data}
                                    required
                                    disablePortal
                                    value={transaction.json?.AddressDestination ?? ""}
                                    onChange={val => setTransaction(prev => ({
                                        ...prev,
                                        json: { ...prev.json, AddressDestination: val?.name ?? val },
                                    }))}
                                    className="form-control border-0 p-0 mb-2"
                                    searchUrl="/places/search/"
                                    minLength={1}
                                    onParentBlur={() => schedulePersist()}
                                    placeholder={data.t.read("supplier_table.address")}
                                />
                            </div>
                        </div>

                        <div className="row" hidden={transaction.json?.type !== 'mileage'}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                Distance :
                            </div>
                            <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                <FormTextField format="distance" locked={true} value={transaction.json?.Distance != null && transaction.json?.Distance !== '' ? String(transaction.json.Distance) : ''} />
                            </div>
                        </div>

                        <div className="row" hidden={transaction.json?.type !== 'mileage'}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {data.t.read("round_trip")} :
                            </div>
                            <Switch className='switch invoice-value ml-3'
                                checked={transaction.json?.isRoundTrip ?? false}
                                disabled={saving || validating}
                                width={70}
                                onColor="#F39800"
                                onHandleColor="#2693e6"
                                boxShadow="0px 1px 5px rgba(0, 0, 0, 0.6)"
                                activeBoxShadow="0px 0px 1px 10px rgba(0, 0, 0, 0.2)"
                                onChange={val => {
                                    if (saving || validating) return;
                                    setTransaction(prev => ({ ...prev, json: { ...prev.json, isRoundTrip: val } }));
                                    schedulePersist();
                                }}
                            />
                        </div>

                        <div className="row" hidden={transaction.json?.type !== 'mileage'}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {`${data.t.read("rate")} / km`} :
                            </div>
                            <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                <FormTextField format="money" locked={true} value={transaction.json?.rate ?? null} />
                            </div>
                        </div>

                        <div className="row" hidden={card_type !== 'p-card'}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {data.t.read("invoice.supplier_name")} :
                            </div>

                            <div className="col-md-5 invoice-value">
                                <FormTextField
                                    format="data" 
                                    locked={saving} 
                                    value={supplierName}
                                    validateChange={val => {
                                        const prev = {...transaction}
                                        const list = prev.json?.ListSupplier || [];
                                        const first = list[0] || {};
                                        const updated_transaction =  {...prev, json: {...prev.json,
                                                ListSupplier: [{ ...first, Name: val }, ...list.slice(1),],
                                            },
                                        };
                                        setTransaction(updated_transaction);
                                        schedulePersist()
                                    }}
                                    searchURL="/suppliers/search/"
                                    searchCallback={(sup, typedText) => supplierChosen(sup, typedText)}
                                    hideAddOperation={!Utils.clientAllowsSupplierCreate(data)}
                                    noAddOperationHint={data.t.read("supplier_not_found_creation_disabled")} />
                            </div>
                        </div>


                        <div className="row" hidden={card_type !== 'p-card'}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {data.t.read("invoice.supplier_number")} :
                            </div>
                            <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                <FormTextField format="data" locked={saving} value={transaction.json?.ListSupplier?.[0]?.Number}
                                    validateChange={val => {
                                        setTransaction(prev => ({
                                            ...prev,
                                            json: {
                                                ...prev.json,
                                                ListSupplier: [
                                                    { ...prev.json.ListSupplier?.[0], Number: val },
                                                    ...prev.json.ListSupplier?.slice(1)
                                                ]
                                            }
                                        }));
                                        schedulePersist()
                                    }}
                                />
                            </div>
                        </div>


                        <div className="row" style={{ display: transaction.json?.type !== 'mileage' ? showStatus(transaction.json?.InvoiceDate) : 'none' }}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {data.t.read("invoice.billing_date")} :
                            </div>
                            <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                <FormTextField format="date" locked={saving} value={transaction.json?.InvoiceDate ? transaction.json?.InvoiceDate : ""} validateChange={val => { setTransaction(prev => ({ ...prev, json: { ...prev.json, InvoiceDate: val } })); schedulePersist() }} />
                            </div>
                        </div>

                        {!data.config.frontend_invoice.hide_due_date && (
                            <div className='row' style={{ display: showStatus(transaction.json?.DueDate) }}>
                                <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                    {data.t.read('invoice.due_date')}  :
                                </div>
                                <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                    <FormTextField format="date" locked={saving} value={transaction.json?.DueDate ? transaction.json?.DueDate : ""} validateChange={val => { setTransaction(prev => ({ ...prev, json: { ...prev.json, DueDate: val } })); schedulePersist() }} />
                                </div>
                            </div>
                        )}

                        {data.config.frontend_invoice.TranDate && (
                            <div className='row' style={{ display: showStatus(transaction.json?.TranDate) }}>
                                <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                    {data.t.read('invoice.transaction_date')}  :
                                </div>
                                <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                    <FormTextField format="date" locked={saving} value={transaction.json?.TranDate ? transaction.json?.TranDate : ""} validateChange={val => { setTransaction(prev => ({ ...prev, json: { ...prev.json, TranDate: val } })); schedulePersist() }} />
                                </div>
                            </div>
                        )}
                        
                        <div className="row" style={{ display: transaction.json?.type !== 'mileage' ? showStatus(transaction.json?.InvoiceNo) : 'none' }}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {data.t.read("invoice.invoice_number")}:
                            </div>
                            <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                <FormTextField format="data" locked={saving} value={transaction.json?.InvoiceNo} validateChange={val => { setTransaction(prev => ({ ...prev, json: { ...prev.json, InvoiceNo: val } })); schedulePersist() }} />
                            </div>
                        </div>

                        <div className="row" style={{ display: transaction.json?.type !== 'mileage' ? showStatus(transaction.json?.Currency) : 'none' }}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {data.t.read("invoice.currency")} :
                            </div>
                            <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                <CurrencySelect
                                    data={data}
                                    disabled={saving}
                                    value={transaction.json?.Currency}
                                    onChange={code => { setTransaction(prev => ({ ...prev, json: { ...prev.json, Currency: code } })); schedulePersist() }}
                                />
                            </div>
                        </div>

                        {Utils.hasExpenseField(data, 'field_expense_type', false) && (
                            <div className="row mb-1">
                                <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                    {data.t.read('review_expense.expense_type.label')} :
                                </div>
                                <Select
                                    inputId="c-card-expense-type"
                                    name="c-card-expense-type"
                                    value={transaction.json?.ExpenseType ? {
                                        value: transaction.json.ExpenseType,
                                        label: data.t.read(`review_expense.expense_type.${transaction.json.ExpenseType}`),
                                    } : null}
                                    options={[
                                        { value: 'client', label: data.t.read('review_expense.expense_type.client') },
                                        { value: 'internal', label: data.t.read('review_expense.expense_type.internal') },
                                    ]}
                                    styles={styleSelect}
                                    className={data.isMobile ? "col-6 invoice-value pl-1" : "pl-1 col-3 invoice-value"}
                                    classNamePrefix="select"
                                    onChange={updateExpenseType}
                                    placeholder={data.t.read('review_expense.expense_type.placeholder')}
                                    isClearable
                                    isDisabled={saving}
                                />
                            </div>
                        )}

                        {transaction.json?.type !== 'mileage' && showStatus(transaction.json?.Category) === 'flex' && (
                            <div className="row mb-1">
                                <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                    {data.t.read("category")} :
                                </div>
                                <Select
                                    inputId="c-card-category"
                                    name="Category"
                                    value={categoryValue}
                                    options={categoryOptions}
                                    formatOptionLabel={formatDeletedImputationLabel}
                                    styles={styleSelect}
                                    className={data.isMobile ? "col-6 invoice-value pl-1" : "pl-1 col-3 invoice-value"}
                                    classNamePrefix="select"
                                    onChange={updateCategory}
                                    placeholder={'Category'}
                                    isLoading={imputationRootsStatus === 'loading'}
                                    isDisabled={saving || imputationRootsStatus !== 'loaded'}
                                />
                            </div>
                        )}

                        {transaction.json?.type !== 'mileage' && ['empty', 'error'].includes(imputationRootsStatus) && (
                            <div className="row mb-1">
                                <div className={data.isMobile ? "col-6" : "col-3"} />
                                <div
                                    className={`${data.isMobile ? "col-6" : "col-3"} small ${imputationRootsStatus === 'error' ? 'text-danger' : 'text-muted'}`}
                                    role={imputationRootsStatus === 'error' ? 'alert' : 'status'}
                                >
                                    {imputationRootsStatus === 'empty' && data.t.read('cc_transactions.category_empty')}
                                    {imputationRootsStatus === 'error' && (
                                        <>
                                            <span>{data.t.read(imputationRootsErrorKey || 'cc_transactions.category_load_error')}</span>
                                            <button
                                                type="button"
                                                className="btn btn-link btn-sm p-0 ml-2"
                                                onClick={retryImputationRoots}
                                            >
                                                {data.t.read('cc_transactions.retry_categories')}
                                            </button>
                                        </>
                                    )}
                                </div>
                            </div>
                        )}

                        {transaction.json?.type !== 'mileage' &&
                            (selectedImputationLeaf || staleImputationLeaf || (categoryEdited && subcategoryOptions.length > 0)) && (
                            <div className="row mb-1">
                                <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                    {data.t.read('review_expense.imputation_subcategory.label')} :
                                </div>
                                <Select
                                    inputId="c-card-subcategory"
                                    name="Subcategory"
                                    value={selectedImputationLeaf ? {
                                        value: imputationDocumentId(selectedImputationLeaf),
                                        label: imputationDisplayLabel(selectedImputationLeaf, data),
                                        row: selectedImputationLeaf,
                                    } : (staleImputationLeaf && transaction?.json?.ImputationLeafId &&
                                        imputationDocumentId(staleImputationLeaf) === String(transaction.json.ImputationLeafId)
                                        ? {
                                            value: imputationDocumentId(staleImputationLeaf),
                                            label: deletedImputationOptionLabel(
                                                staleImputationLeaf,
                                                data.t.read('review_expense.imputation_deleted'),
                                                data
                                            ),
                                            isDeleted: true,
                                            row: staleImputationLeaf,
                                        }
                                        : null)}
                                    options={subcategoryOptions}
                                    formatOptionLabel={formatDeletedImputationLabel}
                                    styles={styleSelect}
                                    className={data.isMobile ? "col-6 invoice-value pl-1" : "pl-1 col-3 invoice-value"}
                                    classNamePrefix="select"
                                    onChange={updateSubcategory}
                                    placeholder={data.t.read('review_expense.imputation_subcategory.label')}
                                    isClearable
                                    isDisabled={saving}
                                />
                            </div>
                        )}

                        {transaction.json?.ExpenseType !== 'internal' && (
                            <div className="row mb-1">
                                <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                    {data.t.read('invoice.is_billable')} :
                                </div>
                                <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                    <Checkbox
                                        data-testid="c-card-is-billable"
                                        disabled={saving}
                                        isChecked={[true, 1].includes(transaction.json?.IsBillable)}
                                        onChange={updateHeaderBillable}
                                    />
                                </div>
                            </div>
                        )}

                        <div className="row" style={{ display: transaction.json?.type !== 'mileage' ? showStatus(transaction.json?.Total) : 'none' }}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {data.t.read("invoice.total")} :
                            </div>
                            <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                <FormTextField format="money" locked={saving} value={transaction.json?.Total ? transaction.json?.Total : ""} validateChange={val => {
                                    if (transaction.json.ListItem?.length === 1) {
                                        updateItem({ ...transaction.json.ListItem[0], Total: val }, true);
                                    } else {
                                        setTransaction(prev => ({ ...prev, json: { ...prev.json, Total: val } }));
                                        schedulePersist();
                                    }
                                }} />
                            </div>
                        </div>

                        <div className="row" style={{ display: transaction.json?.type !== 'mileage' ? showStatus(transaction.json?.Tax) : 'none' }}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {data.t.read("invoice.tax")} :
                            </div>
                            <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                <FormTextField format="money" locked={true} value={transaction.json?.Tax ? transaction.json?.Tax : ""} validateChange={val => { setTransaction(prev => ({ ...prev, json: { ...prev.json, Tax: val, TotalWithTax: val + (transaction.json?.Total ?? 0) } })); schedulePersist() }} />
                            </div>
                        </div>

                        <div className="row" style={{ display: transaction.json?.type !== 'mileage' ? showStatus(transaction.json?.TotalWithTax) : 'none' }}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {data.t.read("invoice.total_with_tax")} :
                            </div>
                            <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                <FormTextField format="money" locked={true} value={transaction.json?.TotalWithTax ? transaction.json?.TotalWithTax : ""} validateChange={val => { setTransaction(prev => ({ ...prev, json: { ...prev.json, TotalWithTax: val } })); schedulePersist() }} />
                            </div>
                        </div>

                        <div className="row" style={{ display: transaction.json?.type !== 'mileage' ? showStatus(transaction.json?.Tip) : 'none' }}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {data.t.read("tips")} :
                            </div>
                            <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                <FormTextField format="money" locked={saving} value={transaction.json?.Tip ? transaction.json?.Tip : ""} validateChange={updateTip} />
                            </div>
                        </div>

                        <div className="row" style={{ display: transaction.json?.type !== 'mileage' ? showStatus(transaction.json?.UnadjustedTotal) : 'none' }}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {data.t.read('invoice.unadjusted_total')} :
                            </div>
                            <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                <FormTextField
                                    data-testid="c-card-unadjusted-total"
                                    format="money"
                                    locked={saving}
                                    value={transaction.json?.UnadjustedTotal ?? ''}
                                    validateChange={updateReceiptTotal}
                                />
                            </div>
                        </div>

                        <div className="row" hidden={card_type === 'p-card'}>
                            <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                {data.t.read("expense")} {data.t.read("invoice.total")} :
                            </div>
                            <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                <FormTextField format="money" locked={true} value={transaction.json?.BigTotalPayed ? transaction.json?.BigTotalPayed : ""} validateChange={val => { setTransaction(prev => ({ ...prev, json: { ...prev.json, BigTotalPayed: val } })); schedulePersist() }} />
                            </div>
                        </div>
                        {Utils.isExportFormat(data, "aderant") && (
                            <div className="row">
                                <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                    {data.t.read("office")} :
                                </div>
                                <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                    <AsyncSelect
                                        name="office"
                                        value={transaction.json?.Office ? { label: transaction.json.Office, value: transaction.json.Office } : null}
                                        defaultOptions
                                        cacheOptions
                                        loadOptions={loadOffices}
                                        styles={Utils.customStyleSelect}
                                        classNamePrefix="select"
                                        isDisabled={saving}
                                        onChange={(choice) => { setTransaction(prev => ({ ...prev, json: { ...prev.json, Office: choice?.value ?? "" } })); schedulePersist(); }}
                                        placeholder={data.t.read("office")}
                                    />
                                </div>
                            </div>
                        )}
                        {Utils.isExportFormat(data, "aderant") && (
                            <>
                                <div className="row">
                                    <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                        {data.t.read("invoice.ap_type")} :
                                    </div>
                                    <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                        <AsyncSelect
                                            name="APTypeId"
                                            value={toSelectOption(transaction.json?.APTypeId)}
                                            defaultOptions
                                            cacheOptions
                                            loadOptions={loadAPTypes}
                                            styles={Utils.customStyleSelect}
                                            classNamePrefix="select"
                                            isDisabled={saving}
                                            isClearable
                                            onChange={(choice) => { setTransaction(prev => ({ ...prev, json: { ...prev.json, APTypeId: choice ?? undefined } })); schedulePersist(); }}
                                            placeholder={data.t.read("invoice.ap_type")}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                        {data.t.read("invoice.jurisdiction")} :
                                    </div>
                                    <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                        <AsyncSelect
                                            name="JurisdictionId"
                                            value={toSelectOption(transaction.json?.JurisdictionId)}
                                            defaultOptions
                                            cacheOptions
                                            loadOptions={loadJurisdictions}
                                            styles={Utils.customStyleSelect}
                                            classNamePrefix="select"
                                            isDisabled={saving}
                                            isClearable
                                            onChange={(choice) => { setTransaction(prev => ({ ...prev, json: { ...prev.json, JurisdictionId: choice ?? undefined } })); schedulePersist(); }}
                                            placeholder={data.t.read("invoice.jurisdiction")}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                        {data.t.read("invoice.hold_date")} :
                                    </div>
                                    <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                        <FormTextField
                                            format="date"
                                            locked={saving}
                                            value={transaction.json?.HoldDate ?? ""}
                                            validateChange={val => { setTransaction(prev => ({ ...prev, json: { ...prev.json, HoldDate: val || undefined } })); schedulePersist(); }}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                        {data.t.read("invoice.invoice_ref")} :
                                    </div>
                                    <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                        <FormTextField
                                            format="text"
                                            locked={saving}
                                            value={transaction.json?.InvoiceRef ?? ""}
                                            validateChange={val => { setTransaction(prev => ({ ...prev, json: { ...prev.json, InvoiceRef: val || undefined } })); schedulePersist(); }}
                                        />
                                    </div>
                                </div>
                                <div className="row">
                                    <div className={data.isMobile ? "col-6 font-weight-bold align-self-center" : "col-3 font-weight-bold align-self-center"}>
                                        {data.t.read("invoice.invoice_narrative")} :
                                    </div>
                                    <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                        <FormTextField
                                            format="text"
                                            locked={saving}
                                            value={transaction.json?.comments ?? ""}
                                            validateChange={val => { setTransaction(prev => ({ ...prev, json: { ...prev.json, comments: val || undefined } })); schedulePersist(); }}
                                        />
                                    </div>
                                </div>
                            </>
                        )}
                        <div className="mt-5" hidden={data.config?.frontend_invoice?.hide_cost_details_table}>
                            <h4 className="hidden-actions-container d-flex justify-content-between">
                                <span>
                                    {getLineItemTableTitle(data, 'cost')}
                                    <span>
                                        <button disabled={saving} className="btn btn-link" onClick={() => addItem(true)}>
                                            <FontAwesomeIcon className="text-success" icon={faPlusSquare} />
                                        </button>
                                    </span>
                                </span>
                                <div className="text-right">
                                    <button className={"btn btn-secondary btn-sm btn-xs " + (billablesSelected.length > 0 ? "" : "faded")} onClick={() => confirmDeleteAction("billable")} >{data.t.read("delete")}</button>
                                </div>
                            </h4>
                            {hasDisbursementItems() &&
                                <table className={data.isMobile ? "table invoice-items-table mt-3 font-xsmall" : "table invoice-items-table mt-3 font-xsmall"}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: data.isMobile ? '20%' : '5%' }}>
                                                <Checkbox disabled={saving} isChecked={(transaction.json?.ListItem || []).filter(item => itemUsesDisbursementCode(item)).length === billablesSelected.length && billablesSelected.length > 0} onChange={e => toggleSelectAll("billable")} />
                                            </th>
                                            <th className="col-3" >Description</th>
                                            <th className="col-2" hidden={data.isMobile} >{data.t.read("office")}</th>
                                            <th className="col-2" hidden={data.isMobile || data.config.frontend_invoice.hide_work_date} >{data.t.read("invoice.work_date")}</th>
                                            <th className="col-3" hidden={data.isMobile}>{data.t.read("invoice.matter_id")}</th>
                                            <th className="col-2" hidden={data.isMobile} >{data.t.read("invoice.cost_type")}</th>
                                            <th className="col-2" hidden={data.isMobile} >{data.t.read("invoice.tax_code")}</th>
                                            <th className="col-2" >{data.t.read("invoice.amount")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            (transaction.json?.ListItem || []).filter(item => itemUsesDisbursementCode(item)).map((item) => {
                                                return <tr key={"invoice-item-" + item._view_id}>
                                                    <td onClick={e => e.stopPropagation()} className="align-middle" >
                                                        <Checkbox isChecked={billablesSelected.findIndex(it => it._view_id === item._view_id) > -1} onChange={e => toggleItemSelected("billable", item)} />
                                                    </td>
                                                    <td className="align-middle">
                                                        <FormTextField format="data" locked={saving} value={item.Description} validateChange={val => updateItem({ ...item, Description: val })} />
                                                    </td>
                                                    <td className="align-middle" hidden={data.isMobile || !Utils.isEngineType(data, "legal")} >
                                                        <AsyncSelect
                                                            name="office"
                                                            value={{ label: item.Office , value: item.Office }}
                                                            defaultOptions cacheOptions loadOptions={loadOffices}
                                                            styles={Utils.customStyleSelect}
                                                            classNamePrefix="select"
                                                            onChange={(choice) => updateItem({ ...item, Office: choice?.value ?? "" })}
                                                            components={{ DropdownIndicator: null, IndicatorSeparator: null }}
                                                            placeholder={data.t.read("office")} />
                                                    </td>
                                                    <td className="align-middle" hidden={data.isMobile || data.config.frontend_invoice.hide_work_date} >
                                                        <FormTextField format="date" locked={saving} value={item.WorkDate ?? ""} validateChange={val => updateItem({ ...item, WorkDate: val })} />
                                                    </td>
                                                    <td className="align-middle" hidden={data.isMobile}>
                                                        <div className="d-flex align-items-center">
                                                            <AutoCompleteField
                                                                disablePortal
                                                                value={item.MatterId ?? ""}
                                                                onChange={val => updateItem({ ...item, MatterId: val.name ?? val })}
                                                                className="form-control border-0"
                                                                searchUrl="/matters/search/"
                                                                // XPEN-3322: the picker edits the collapsed number; the full one is
                                                                // restored before it is stored, so MatterId still resolves at export.
                                                                formatEditValue={formatMatterNumber}
                                                                parseEditValue={expandMatterNumber}
                                                                minLength={1}
                                                                placeholder={data.t.read("invoice.matter_id")}
                                                                formatLabel={(m) => formatMatterLabelFromData(data, m)} />
                                                            {shouldShowActivityInlineTrigger(data) && <div onClick={() => { setShowAdditionalFieldsModal(true); setWorkItem(item); setAdditionalFieldsFilter(buildActivityAdditionalFieldsFilter(data)); }}>
                                                                <HoverInfoCard
                                                                    info={<FontAwesomeIcon icon={faExclamationTriangle} className='text-primary pointer ' size={"2x"} />}
                                                                    position="pos-top-right"
                                                                    additionalInfo={
                                                                        <>
                                                                            <div style={{ minWidth: "200px", textTransform: "none" }}>
                                                                                <strong className="mr-2">{data.t.read('validation_error')} </strong>
                                                                                <strong>{data.t.read('invoice.activity')}</strong>
                                                                                <div>
                                                                                    <p className="mb-0 mt-2 italic">
                                                                                        {data.t.read('click_to')} {data.t.read('select')} {data.t.read('invoice.activity')}
                                                                                    </p>
                                                                                </div>
                                                                            </div>
                                                                        </>
                                                                    }
                                                                    shouldDisplayIcon={(callback) => {
                                                                        getMatterInfo(item.MatterId, (matterInfo) => {
                                                                            if (!matterInfo) return callback(false);
                                                                                                    
                                                                            const { pta_group_cost } = matterInfo;

                                                                            if (!pta_group_cost) return callback(false);


                                                                            // Matter has PTA group but no activity yet
                                                                            if (pta_group_cost && !item.Activity) return callback(true);

                                                                            // Compare PTA group / cost mismatches
                                                                            if (pta_group_cost !== item.Activity.activity_list) {
                                                                                return callback(true);
                                                                            }

                                                                            callback(false);
                                                                        });
                                                                    }}
                                                                />
                                                            </div>}
                                                        </div>
                                                    </td>
                                                    <td className="align-middle" hidden={data.isMobile}>
                                                        <AutoCompleteField
                                                            disablePortal
                                                            value={item.CostType ?? ""}
                                                            onChange={val => {
                                                                const costTypeValue = val?.name ?? val;
                                                                updateLineItemAndDescription(item._view_id, "CostType", costTypeValue, val);
                                                            }}
                                                            className="form-control border-0"
                                                            searchUrl="/cost_type/search/"
                                                            minLength={1}
                                                            placeholder={data.t.read("invoice.cost_type")} />
                                                    </td>
                                                    <td className="align-middle" hidden={data.isMobile}>
                                                        <TaxCodeSelector
                                                            taxCodes={taxeCodes}
                                                            value={item.TaxCode ?? defaultTaxCode}
                                                            disabled={saving}
                                                            onChange={(taxCode) => updateItem({ ...item, TaxCode: taxCode }, true)}
                                                        />
                                                    </td>

                                                    <td>
                                                        <div className="position-relative">
                                                            <FormTextField format="money" locked={saving} value={item.Total} validateChange={val => updateItem({ ...item, Total: val }, true)} />
                                                            <div className="item-actions">
                                                                {Utils.isExportFormat(data, "aderant") && (
                                                                    <button
                                                                        className="btn btn-sm btn-link"
                                                                        onClick={() => {
                                                                            setShowAdditionalFieldsModal(true);
                                                                            setWorkItem(item);
                                                                            setAdditionalFieldsFilter(buildBillableAdditionalFieldsFilter(data, {
                                                                                model: transaction.json?.Model,
                                                                            }));
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faEllipsisH} />
                                                                    </button>
                                                                )}
                                                                <button className="btn btn-sm btn-link" onClick={() => setConfirmDeleteViewId(item._view_id)}>
                                                                    <FontAwesomeIcon className="text-danger" icon={faWindowClose} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            })
                                        }
                                    </tbody>
                                </table>}
                        </div>

                        <div className="mt-5">
                            <h4 className="hidden-actions-container d-flex justify-content-between">
                                <span>
                                    {getLineItemTableTitle(data, 'gl')}
                                    <span>
                                        <button disabled={saving} className="btn btn-link" onClick={() => addItem(false)}>
                                            <FontAwesomeIcon className="text-success" icon={faPlusSquare} />
                                        </button>
                                    </span>
                                </span>
                                <div className="text-right">
                                    <button className={"btn btn-secondary btn-sm btn-xs " + (nonBillablesSelected.length > 0 ? "" : "faded")} onClick={() => confirmDeleteAction("non-billable")} >{data.t.read("delete")}</button>
                                </div>
                            </h4>
                            {hasGlItems() &&
                                <table className={data.isMobile ? "table invoice-items-table mt-3 font-xsmall" : "table invoice-items-table mt-3 font-xsmall"}>
                                    <thead>
                                        <tr>
                                            <th style={{ width: data.isMobile ? '20%' : '5%' }}>
                                                <Checkbox disabled={saving} isChecked={(transaction.json?.ListItem || []).filter(item => !itemUsesDisbursementCode(item)).length === nonBillablesSelected.length && nonBillablesSelected.length > 0} onChange={e => toggleSelectAll("non-billable")} />
                                            </th>
                                            <th className="col-3" >Description</th>
                                            <th className="col-2" hidden={data.isMobile || !Utils.isEngineType(data, "legal")} >{data.t.read((Utils.isExportFormat(data, "elite") && data?.config?.gl_account_segment_configuration?.GLOffice?.active === true) ? "invoice.office" : "office")}</th>
                                            <th hidden={data.isMobile} className="col-3">{data.t.read("invoice.gl_nat")}</th>
                                            <th className="col-3" hidden={data.isMobile}>{data.t.read("invoice.tax_code")}</th>
                                            <th className="col-3">{data.t.read("invoice.amount")}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {
                                            (transaction.json?.ListItem || []).filter(item => !itemUsesDisbursementCode(item)).map((item) => {
                                                return <tr key={"invoice-item-" + item._view_id}>
                                                    <td onClick={e => e.stopPropagation()} className="align-middle">
                                                        <Checkbox isChecked={nonBillablesSelected.findIndex(it => it._view_id === item._view_id) > -1} onChange={e => toggleItemSelected("non-billable", item)} />
                                                    </td>
                                                    <td className="align-middle" >
                                                        <FormTextField format="data" locked={saving} value={item.Description} validateChange={val => updateItem({ ...item, Description: val })} />
                                                    </td>
                                                    <td className="align-middle" hidden={data.isMobile || !Utils.isEngineType(data, "legal")} >
                                                        {(Utils.isExportFormat(data, "elite") && data?.config?.gl_account_segment_configuration?.GLOffice?.active === true) ?
                                                            <AutoCompleteField
                                                                disablePortal
                                                                value={item.GLOffice ?? ""}
                                                                onChange={(val) => updateItem({ ...item, GLOffice: val?.name ?? val ?? "" })}
                                                                className="form-control border-0 pl-0"
                                                                searchUrl="/offices/search/"
                                                                minLength={1}
                                                                placeholder={data.t.read("invoice.office")} />
                                                            :
                                                            <AsyncSelect
                                                                name="office"
                                                                value={{ label: item.Office, value: item.Office }}
                                                                defaultOptions cacheOptions loadOptions={loadOffices}
                                                                className={`border-0`}
                                                                styles={Utils.customStyleSelect}
                                                                classNamePrefix="select"
                                                                components={{ DropdownIndicator: null, IndicatorSeparator: null }}
                                                                onChange={(choice) => updateItem({ ...item, Office: choice?.value ?? "" })}
                                                                placeholder={data.t.read("office")} />
                                                        }
                                                    </td>
                                                    <td className="align-middle" hidden={data.isMobile}>
                                                        <AutoCompleteField
                                                            disablePortal
                                                            value={item.GLNatural ?? ""}
                                                            onChange={val => {
                                                                const glNaturalValue = val?.name ?? val;
                                                                updateLineItemAndDescription(item._view_id, "GLNatural", glNaturalValue, val);
                                                            }}
                                                            className="form-control border-0 pl-0"
                                                            searchUrl="/gl/nat/search/"
                                                            minLength={1}
                                                            placeholder={data.t.read("invoice.gl_nat")} />
                                                    </td>
                                                    <td className="align-middle" hidden={data.isMobile}>
                                                        <TaxCodeSelector
                                                            taxCodes={taxeCodes}
                                                            value={item.TaxCode ?? defaultTaxCode}
                                                            disabled={saving}
                                                            onChange={(taxCode) => updateItem({ ...item, TaxCode: taxCode }, true)}
                                                        />
                                                    </td>
                                                    <td>
                                                        <div className="position-relative">
                                                            <FormTextField format="money" locked={saving} value={item.Total} validateChange={val => updateItem({ ...item, Total: val }, true)} />
                                                            <div className="item-actions">
                                                                {Utils.isExportFormat(data, "aderant") && (
                                                                    <button
                                                                        className="btn btn-sm btn-link"
                                                                        onClick={() => {
                                                                            setShowAdditionalFieldsModal(true);
                                                                            setWorkItem(item);
                                                                            setAdditionalFieldsFilter(buildGlAdditionalFieldsFilter(data, {
                                                                                isLegalEngine: Utils.isEngineType(data, 'legal'),
                                                                            }));
                                                                        }}
                                                                    >
                                                                        <FontAwesomeIcon icon={faEllipsisH} />
                                                                    </button>
                                                                )}
                                                                <button className="btn btn-sm btn-link" onClick={() => setConfirmDeleteViewId(item._view_id)}>
                                                                    <FontAwesomeIcon className="text-danger" icon={faWindowClose} />
                                                                </button>
                                                            </div>
                                                        </div>
                                                    </td>
                                                </tr>
                                            })
                                        }
                                    </tbody>
                                </table>}
                        </div>

                        <h4 className="mt-5" style={{ display: showStatus(transaction.json?.Tax1) === "flex" || showStatus(transaction.json?.Tax1) === "flex" ? "block" : "none" }}>{data.t.read("invoice.tax_details")}</h4>

                        {
                            !data.config.hide_tax_1_field && <div className="row" style={{ display: showStatus(transaction.json?.Tax1) }}>
                                <div className={data.isMobile ? "col-6 font-weight-bold" : "col-2 font-weight-bold"}>
                                    {data.t.read("invoice.tax")} 1 :
                                </div>
                                <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                    <FormTextField format="money" locked={saving} value={transaction.json?.Tax1 ? transaction.json.Tax1 : ""} validateChange={val => { setTransaction(prev => ({ ...prev, json: { ...prev.json, Tax1: val, Tax: val + (transaction.json?.Tax2 ?? 0), TotalWithTax: val + (transaction.json?.Tax2 ?? 0) + (transaction.json?.Total ?? 0) } })); schedulePersist() }} />
                                </div>
                            </div>
                        }
                        {
                            !data.config.hide_tax_2_field && <div className="row" style={{ display: showStatus(transaction.json?.Tax2) }}>
                                <div className={data.isMobile ? "col-6 font-weight-bold" : "col-2 font-weight-bold"}>
                                    {data.t.read("invoice.tax")} 2 :
                                </div>
                                <div className={data.isMobile ? "col-6 invoice-value" : "col-3 invoice-value"}>
                                    <FormTextField format="money" locked={validating | saving} value={transaction.json?.Tax2 ? transaction.json.Tax2 : ""} validateChange={val => { setTransaction(prev => ({ ...prev, json: { ...prev.json, Tax2: val, Tax: val + (transaction.json?.Tax1 ?? 0), TotalWithTax: val + (transaction.json?.Tax1 ?? 0) + (transaction.json?.Total ?? 0) } })); schedulePersist() }} />
                                </div>
                            </div>
                        }
                        

                        {hasExpenseCommentIdentity(transaction) ? <CommentsPanel invoiceId={transaction.expense_uid} subjectType="expense"
                            refreshKey={transaction.json} data={data} legacyText={legacyCommentsText(transaction)} onDraftChange={onDraftChange} />
                            : <h4 className="mt-5">{data.t.read("comments")}</h4>}
                        <div>
                            {hasExpenseCommentIdentity(transaction) ? transaction.status_details && <div className="mt-3">
                                <h5>{data.t.read('comment_ui.status_details')}</h5>
                                <div style={{ whiteSpace: 'pre-wrap' }}>{transaction.status_details}</div>
                            </div> : <FormTextAreaAutoSaved locked={validating || saving} value={transaction.status_details} validateChange={val => { setTransaction(prev => ({ ...prev, status_details: val })); schedulePersist() }} />}
                        </div>

                        {
                            <div className="mt-5 d-flex justify-content-between">
                                <div>
                                    <button hidden={transaction.json?.type === 'mileage'} className="btn btn-sm btn-secondary mr-2" onClick={() => setShowConfirmRestartProcess(true)}>
                                        {data.t.read("invoice.restart_processing")}
                                    </button>
                                </div>
                                {account_status === 'Submitted' && (Utils.userHasRole(data, ['Admin', 'Manager']) || AccessControl.hasActionAccess(data, "canValidateCardTx")) &&
                                    <button className="btn btn-primary" disabled={validating | saving} onClick={() => validateTransaction()}>
                                        {data.t.read("validate")}
                                        {validating && <FontAwesomeIcon className="ml-2 text-secondary infinite-rotate faded" icon={faSync} />}
                                    </button>
                                }

                                {Utils.userHasPageAccess(data, "approval") && (Utils.userHasRole(data, ['Admin', 'Approver']) || AccessControl.hasActionAccess(data, "canApproveOrDeny")) &&
                                    <div className="text-right">
                                        <button className={"btn ${data.isMobile ? 'col-8' : ''}` m-1  btn-primary"} hidden={transaction.status !== "Need_Approval"}
                                            onClick={() => approveTransaction()}>
                                            {data.t.read("approve")}
                                            {saving && action === 'approving' && <FontAwesomeIcon className="ml-2 text-secondary infinite-rotate faded" icon={faSync} />}
                                        </button>

                                        <button className="btn ${data.isMobile ? 'col-8' : ''}` m-1 btn-danger " hidden={transaction.status !== "Need_Approval"} onClick={() => setShowDisapproval(true)}>
                                            {data.t.read("disapprove")}
                                        </button>
                                    </div>
                                }

                            </div>
                        }
                    </div>
                }
                {
                    currentView === "history" && <LogHistory data={data} statement_id={statement_id} checksum={transaction.checksum} />
                }
            </div>


            {
                showConfirmRestartProcess === true &&
                <Modal title={data.t.read("confirm")} cancelAction={() => setShowConfirmRestartProcess(false)} okAction={() => restartProcess()} okText={data.t.read("yes")} cancelText={data.t.read("no")}>
                    {data.t.read("invoice.restart_processing")} ?
                </Modal>

            }
            {
                confirmDeleteItems != null &&
                <Modal title={data.t.read("confirm")} cancelAction={() => setConfirmDeleteItems()} okAction={() => deleteItems()} okText={data.t.read("delete")} cancelText={data.t.read("cancel")}>
                    {data.t.read("delete")} ?
                </Modal>

            }
            {
                confirmDeleteViewId != null &&
                <Modal title={data.t.read("confirm")} cancelAction={() => setConfirmDeleteViewId(null)} okAction={() => deleteItem(confirmDeleteViewId)} okText={data.t.read("delete")} cancelText={data.t.read("cancel")}>
                    {data.t.read("delete")} ?
                </Modal>

            }
            {
                savingError != null &&
                <Modal title={data.t.read("error")} okAction={() => setSavingError(null)} okText={"ok"}>
                    <div className="text-danger">
                        {savingError}
                    </div>
                </Modal>

            }
            {
                showDisapproval &&
                <Modal overflow="visible" title={data.t.read("denial_cause_placeholder")} cancelAction={() => setShowDisapproval(false)} okAction={() => disApproveTransaction()} okText={data.t.read("submit")} cancelText={data.t.read("cancel")}>
                    <input className='form-control' type="text" onChange={e => setCauseDenial(e.target.value)} />
                </Modal>
            }
            {
                promptNewSupplier &&
                <SupplierForm
                    supplier={newSupplier}
                    cancelSupplierModal={() => setPromptNewSupplier(false)}
                    setShowSupplierModal={show => setPromptNewSupplier(show)}
                    callerCallback={(supplier) => supplierChosen(supplier, null)}
                />
            }
            {
                showAdditionalFieldsModal &&
                <Modal
                    title={data.t.read("invoice.additional_fields")}
                    width={400}
                    onClose={() => { setShowAdditionalFieldsModal(false); setAdditionalFieldsFilter([]); }}
                    secondaryAction={() => { setShowAdditionalFieldsModal(false); setAdditionalFieldsFilter([]); }}
                    secondaryText={data.t.read("close")}
                >
                    <AdditionalFieldsModal
                        onClose={() => { setShowAdditionalFieldsModal(false); setAdditionalFieldsFilter([]); }}
                        workItem={workItem}
                        saving={saving}
                        locked={false}
                        updateInvoice={updateItemForModal}
                        fields={[...additionalFieldsFilter]}
                    />
                </Modal>
            }
        </div>
}

export default observer(Transaction);
